import React, { useEffect, useState, useCallback, useRef } from 'react';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { Sizes } from '@newcore/ui/enums/sizes';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { Text } from '@newcore/ui/typography';
import fingerprintIcon from '@/assets/fingerprint.svg';
import { LoginCard } from '@/components/login-card';
import { LoginButton } from '@/components/ui/login-button';
import { Page } from '@/components/ui/page';
import { StatusMessage } from '@/components/ui/status-message';
import { useLanguage } from '@/contexts/language-context';
import type { UseMfaOrchestratorReturn } from '@/hooks/use-mfa-orchestrator.types';
import {
  isWebAuthnSupported,
  base64urlToUint8Array,
  arrayBufferToBase64url,
  parseWebAuthnError,
  toAuthenticatorTransports,
} from '@/utils/webauthn.utils';

interface WebAuthnMetadata {
  challenge: string;
  rp_id: string;
  allow_credentials: Array<{
    id: string;
    type: string;
    transports?: string[] | null;
  }>;
  timeout?: number;
  user_verification?: string;
}

interface WebAuthnFactorProps {
  submitResponse: UseMfaOrchestratorReturn['submitResponse'];
  metadata?: WebAuthnMetadata | Record<string, unknown> | null;
  hasAlternatives?: boolean;
  onShowAlternatives?: () => void;
  username?: string;
  onBack?: () => void;
  isEscalationFlow?: boolean;
}

/**
 * Type guard to check if metadata has the required WebAuthn fields
 */
const isWebAuthnMetadata = (
  meta: WebAuthnMetadata | Record<string, unknown> | null | undefined
): meta is WebAuthnMetadata => {
  if (!meta) {
    return false;
  }

  return (
    typeof (meta as WebAuthnMetadata).challenge === 'string' &&
    typeof (meta as WebAuthnMetadata).rp_id === 'string' &&
    Array.isArray((meta as WebAuthnMetadata).allow_credentials)
  );
};

const WEBAUTHN_UNSUPPORTED_ERROR =
  'WebAuthn is not supported in your browser. Please use a modern browser like Chrome, Safari, or Firefox.';
const WEBAUTHN_METADATA_MISSING_ERROR =
  'Authentication data is missing. Please try logging in again.';

const getWebAuthnPreconditionError = (
  meta: WebAuthnMetadata | Record<string, unknown> | null | undefined
): string | null => {
  if (!isWebAuthnSupported()) {
    return WEBAUTHN_UNSUPPORTED_ERROR;
  }

  if (!isWebAuthnMetadata(meta)) {
    return WEBAUTHN_METADATA_MISSING_ERROR;
  }

  return null;
};

interface WebAuthnFactorLoadingViewProps {
  hasAlternatives?: boolean;
  onShowAlternatives?: () => void;
  username?: string;
  onBack?: () => void;
  isEscalationFlow?: boolean;
}

/** Loading state of WebAuthn factor (spinner + fingerprint). */
export function WebAuthnFactorLoadingView({
  hasAlternatives,
  onShowAlternatives,
  username,
  onBack,
  isEscalationFlow,
}: WebAuthnFactorLoadingViewProps) {
  return (
    <Page className='flex flex-col justify-center items-center'>
      <LoginCard
        hasAlternatives={hasAlternatives}
        onShowAlternatives={onShowAlternatives}
        username={username}
        onBack={onBack}
        isEscalationFlow={isEscalationFlow}
      >
        <div className='flex flex-col gap-6'>
          <div className='flex flex-col gap-4 items-center'>
            <div className='relative w-16 h-16'>
              <div
                className='absolute inset-0 rounded-full border border-primary border-t-brand animate-spin'
                aria-hidden
              />
              <div className='absolute inset-0 flex items-center justify-center'>
                <img
                  src={fingerprintIcon}
                  alt=''
                  className='h-10 w-10 object-contain animate-breathe'
                  aria-hidden
                />
              </div>
            </div>
            <div className='flex flex-col gap-1 text-center'>
              <Text as='h1' size={TextSizes.MD} weight={FontWeights.BOLD}>
                Verify using your passkey
              </Text>
              <Text size={TextSizes.SM} className='text-secondary'>
                Use Touch ID, Face ID, Windows Hello, or a security key to
                authenticate
              </Text>
            </div>
          </div>
        </div>
      </LoginCard>
    </Page>
  );
}

export const WebAuthnFactor: React.FC<WebAuthnFactorProps> = ({
  submitResponse,
  metadata,
  hasAlternatives,
  onShowAlternatives,
  username,
  onBack,
  isEscalationFlow,
}) => {
  const { t } = useLanguage();
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasAttempted, setHasAttempted] = useState(false);
  const isAuthenticatingRef = useRef(false);

  /**
   * Perform WebAuthn authentication ceremony
   */
  const authenticate = useCallback(async () => {
    // Prevent double-triggering - critical for WebAuthn
    if (isAuthenticatingRef.current) {
      console.info('WebAuthn: Already authenticating, skipping duplicate call');

      return;
    }

    isAuthenticatingRef.current = true;

    if (!isWebAuthnSupported()) {
      setError(WEBAUTHN_UNSUPPORTED_ERROR);
      isAuthenticatingRef.current = false;

      return;
    }

    if (!isWebAuthnMetadata(metadata)) {
      setError(WEBAUTHN_METADATA_MISSING_ERROR);
      isAuthenticatingRef.current = false;

      return;
    }

    setIsAuthenticating(true);
    setError(null);
    setHasAttempted(true);

    try {
      // Build PublicKeyCredentialRequestOptions
      // Type guard ensures these fields exist and are of the correct type
      const publicKeyOptions: PublicKeyCredentialRequestOptions = {
        challenge: base64urlToUint8Array(metadata.challenge)
          .buffer as ArrayBuffer,
        timeout: metadata.timeout ?? 60000,
        rpId: metadata.rp_id,
        allowCredentials: metadata.allow_credentials.map(cred => ({
          id: base64urlToUint8Array(cred.id).buffer as ArrayBuffer,
          type: 'public-key' as const,
          transports: toAuthenticatorTransports(cred.transports),
        })),
        userVerification: (metadata.user_verification ??
          'preferred') as UserVerificationRequirement,
      };

      // Call navigator.credentials.get()
      const credential = (await navigator.credentials.get({
        publicKey: publicKeyOptions,
      })) as PublicKeyCredential | null;

      if (!credential) {
        throw new Error('No credential returned from authenticator');
      }

      const response = credential.response as AuthenticatorAssertionResponse;

      // Convert response to base64url strings
      const payload = {
        credential_id: arrayBufferToBase64url(credential.rawId),
        authenticator_data: arrayBufferToBase64url(response.authenticatorData),
        client_data_json: arrayBufferToBase64url(response.clientDataJSON),
        signature: arrayBufferToBase64url(response.signature),
      };

      // Submit the assertion to the backend
      await submitResponse(payload);

      // Reset state and ref on success (component may not unmount if flow continues)
      setIsAuthenticating(false);
      isAuthenticatingRef.current = false;
    } catch (err) {
      const webauthnError = parseWebAuthnError(err);
      console.error('Authentication error:', webauthnError);

      // Set user-friendly error message
      setError(webauthnError.message);
      setIsAuthenticating(false);
      isAuthenticatingRef.current = false;
    }
  }, [metadata, submitResponse]);

  // Auto-trigger authentication on mount
  useEffect(() => {
    if (!hasAttempted && !isAuthenticating && isWebAuthnMetadata(metadata)) {
      // Mount-time WebAuthn ceremony; cannot be derived without dropping auto-prompt.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      authenticate();
    }
  }, [hasAttempted, isAuthenticating, metadata, authenticate]);

  const handleRetry = () => {
    setError(null);
    isAuthenticatingRef.current = false;
    setIsAuthenticating(false);
    setHasAttempted(false);
    authenticate();
  };

  // Surface precondition failures at render time so they never require a manual click.
  // Precondition takes precedence over any stale runtime error — a missing-metadata
  // blocker is the current truth even if a prior auth attempt errored.
  const preconditionError = getWebAuthnPreconditionError(metadata);
  const effectiveError = preconditionError ?? error;

  if (effectiveError) {
    return (
      <Page className='flex flex-col justify-center items-center'>
        <LoginCard
          hasAlternatives={hasAlternatives}
          onShowAlternatives={onShowAlternatives}
          username={username}
          onBack={onBack}
          isEscalationFlow={isEscalationFlow}
        >
          <div className='flex flex-col gap-6'>
            <div className='flex flex-col gap-1'>
              <Text as='h1' size={TextSizes.MD} weight={FontWeights.BOLD}>
                Verify using your passkey
              </Text>
              <Text size={TextSizes.SM} className='text-secondary'>
                Use Touch ID, Face ID, Windows Hello, or a security key to
                authenticate
              </Text>
            </div>

            <div className='flex flex-col gap-4'>
              <StatusMessage>{effectiveError}</StatusMessage>

              {!preconditionError && (
                <LoginButton
                  data-testid='retry-webauthn-button'
                  type='button'
                  size={Sizes.LG}
                  className='w-full'
                  onClick={handleRetry}
                >
                  {t('auth.tryAgain')}
                </LoginButton>
              )}
            </div>
          </div>
        </LoginCard>
      </Page>
    );
  }

  return (
    <WebAuthnFactorLoadingView
      hasAlternatives={hasAlternatives}
      onShowAlternatives={onShowAlternatives}
      username={username}
      onBack={onBack}
      isEscalationFlow={isEscalationFlow}
    />
  );
};
